import React, { useState, useRef } from "react";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";

import { Box, HStack, VStack, Error } from "../components/utils";
import {
  ButtonSize,
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../pages/quickRound/CTAButtonComponents";
import { Label, Required } from "../components/shared/InputField";
import { PopupHeader } from "../components/Headings";
import { ReactDropzone } from "../utils/dropzone";
import { BeneficiaryModel } from "../types/Shareholder";
import {
  useUploadBeneficialCertificateDocument,
  useDeleteBeneficialCertificateDocument,
  useGetBeneficialInvestorDetails,
} from "../queries/shareholders";
import { downloadS3File } from "../utils/DownloadFile";
import { formatDisplayDate } from "../utils/date";
import EmptyTableIllustration from "../shared/EmptyTableIllustration";

interface BeneficiaryDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  beneficiary: BeneficiaryModel;
  investorId: string;
}

interface DocumentUploadForm {
  files: File[];
}

export function BeneficiaryDocumentModal({
  isOpen,
  onClose,
  beneficiary,
  investorId,
}: BeneficiaryDocumentModalProps) {
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const { mutate: uploadDocument } = useUploadBeneficialCertificateDocument();
  const { mutate: deleteDocument } = useDeleteBeneficialCertificateDocument();

  // Get beneficiary details to access documents
  const { data: beneficiaryDetails, refetch } =
    useGetBeneficialInvestorDetails(investorId);

  const uploadSchema = Yup.object({
    files: Yup.array()
      .min(1, "Please select at least one file")
      .required("Files are required"),
  });

  const formik = useFormik<DocumentUploadForm>({
    initialValues: {
      files: [],
    },
    validationSchema: uploadSchema,
    onSubmit: (values) => {
      handleUpload(values.files);
    },
  });

  const handleUpload = (files: File[]) => {
    if (!files || files.length === 0) {
      toast("Please select files to upload", { type: "error" });
      return;
    }

    setLoading(true);

    files.forEach((file) => {
      const formData = new FormData();
      formData.append("beneficialInvestorId", investorId);
      formData.append("certificateId", beneficiary.id);
      formData.append("file", file);

      uploadDocument(
        { formData },
        {
          onSuccess: () => {
            toast("Document uploaded successfully!", {
              type: "success",
              autoClose: 2000,
            });
            refetch();
            setShowUploadForm(false);
            formik.resetForm();
            setLoading(false);
          },
          onError: (error: any) => {
            toast(
              error?.response?.data?.errorMessage ||
                "Failed to upload document",
              { type: "error", autoClose: 2000 }
            );
            setLoading(false);
          },
        }
      );
    });
  };

  const handleDelete = (id: string) => {
    const documentId = id;

    deleteDocument(
      { documentId, investorId },
      {
        onSuccess: () => {
          toast("Document deleted successfully!", {
            type: "success",
            autoClose: 2000,
          });
          refetch();
        },
        onError: (error: any) => {
          toast(
            error?.response?.data?.errorMessage || "Failed to delete document",
            { type: "error", autoClose: 2000 }
          );
        },
      }
    );
  };

  const handleDownload = (url: string, name: string) => {
    downloadS3File(url, name);
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "vscode-icons:file-type-pdf2";
      case "doc":
      case "docx":
        return "vscode-icons:file-type-word2";
      case "xls":
      case "xlsx":
        return "vscode-icons:file-type-excel2";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "vscode-icons:file-type-image";
      default:
        return "vscode-icons:default-file";
    }
  };

  const documents =
    beneficiaryDetails?.shareCertificateDetails.length! > 0
      ? beneficiaryDetails?.shareCertificateDetails[0].documents || []
      : [];

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <Box className="px-8 py-6 bg-white rounded-lg">
        <PopupHeader
          text={`Documents - ${beneficiary.name}`}
          onClick={onClose}
        />

        <VStack className="mt-6 gap-6">
          {/* Header with upload button */}
          <HStack className="flex justify-end">
            {!showUploadForm && (
              <PrimaryCTAButton
                buttonSize={ButtonSize.SMALL}
                onClick={() => setShowUploadForm(true)}
                className="flex items-center gap-2"
              >
                <Icon icon="material-symbols:upload" width={16} />
                Upload Document
              </PrimaryCTAButton>
            )}
          </HStack>

          {/* Upload Form */}
          {showUploadForm && (
            <Box className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <form onSubmit={formik.handleSubmit}>
                <VStack className="gap-4">
                  <div>
                    <Label className="text-sm font-medium">
                      Upload Files <Required />
                    </Label>
                    <ReactDropzone
                      updateFiles={(files: File[]) => {
                        formik.setFieldValue("files", files);
                      }}
                    />
                    {formik.touched.files && formik.errors.files && (
                      <Error text={formik.errors.files as string} />
                    )}
                  </div>

                  <HStack className="justify-end gap-3">
                    <SecondaryCTAButton
                      type="button"
                      buttonSize={ButtonSize.SMALL}
                      onClick={() => {
                        setShowUploadForm(false);
                        formik.resetForm();
                      }}
                    >
                      Cancel
                    </SecondaryCTAButton>
                    <PrimaryCTAButton
                      type="submit"
                      buttonSize={ButtonSize.SMALL}
                      loading={loading}
                      disabled={loading}
                    >
                      Upload
                    </PrimaryCTAButton>
                  </HStack>
                </VStack>
              </form>
            </Box>
          )}

          {/* Documents List */}
          <Box className="border border-gray-200 rounded-lg">
            {documents.length === 0 ? (
              <Box className="py-12">
                <EmptyTableIllustration text="No documents uploaded yet. Upload documents to get started." />
              </Box>
            ) : (
              <VStack className="divide-y divide-gray-200">
                {documents.map((doc, index) => (
                  <HStack
                    key={index}
                    className="p-4 justify-between items-center hover:bg-gray-50"
                  >
                    <HStack className="items-center gap-3 flex-1">
                      <Icon
                        icon={getFileIcon(doc.name)}
                        width={32}
                        height={32}
                      />
                      <VStack className="gap-1 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {doc.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Uploaded on {formatDisplayDate(doc.createdAt)}
                        </p>
                      </VStack>
                    </HStack>

                    <HStack className="gap-2">
                      <button
                        type="button"
                        onClick={() => handleDownload(doc.url, doc.name)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Download"
                      >
                        <Icon icon="material-symbols:download" width={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(doc.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Icon icon="material-symbols:delete" width={16} />
                      </button>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            )}
          </Box>

          {/* Footer */}
          <HStack className="justify-end">
            <SecondaryCTAButton
              buttonSize={ButtonSize.MEDIUM}
              onClick={onClose}
            >
              Close
            </SecondaryCTAButton>
          </HStack>
        </VStack>
      </Box>
    </Dialog>
  );
}
